import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfoPage from "../InfoPage";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import LocationAutocomplete from "../../components/LocationAutocomplete";
import { cleanHandle } from "../../utils/streamHelpers";
import Icon from "../../components/Icon";
import ReviewCard from "./ReviewCard";
import { uploadFile } from "../../utils/fileUpload";
import { handleCloudinaryImageError } from "../../utils/cloudinary";
import { FavoriteInline } from '../../components/FavoriteToggle';
import { toggleFavorite } from '../../utils/favorites';
import IntroVideoPreview from "../../components/IntroVideoPreview";
import ShowcaseGrid from "../../components/ShowcaseGrid";
import CollabBadge from "../../components/CollabBadge";
import { usePersona } from "../../contexts/PersonaContext";
import './Profile.sass';

// Trust-signal stat strip (PRD 4): completed Postaway collabs, average
// rating, review count — Airbnb-Superhost-style column near the name.
// PRD 5's positive-only response-time label slots into the same strip.
const ProfileStatBlock = ({ completedCollabs, averageRating, totalReviews, responseTimeLabel }) => {
  const collabs = parseInt(completedCollabs, 10) || 0;
  const reviews = parseInt(totalReviews, 10) || 0;
  const rating = averageRating != null ? parseFloat(averageRating) : null;

  const stats = [];
  // The collab count is the headline selling point — red, bigger, unmissable.
  stats.push({
    key: 'collabs',
    value: <span style={{ color: '#dc1d08', fontSize: 24, fontWeight: 800 }}>{collabs}</span>,
    label: collabs === 1 ? 'Postaway Collab' : 'Postaway Collabs',
  });
  if (reviews > 0 && rating != null && !Number.isNaN(rating)) {
    stats.push({
      key: 'rating',
      value: (
        <>
          {Number.isInteger(rating) ? rating.toFixed(1) : rating.toFixed(1)}
          <span style={{ color: '#dc1d08', marginLeft: 2 }}>★</span>
        </>
      ),
      label: 'Rating',
    });
  }
  if (reviews > 0) {
    stats.push({ key: 'reviews', value: String(reviews), label: reviews === 1 ? 'Review' : 'Reviews' });
  }

  // Nothing meaningful to show — render nothing rather than a row of zeros.
  if (collabs === 0 && reviews === 0 && !responseTimeLabel) return null;

  return (
    <div style={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      margin: '10px auto 4px',
      background: '#ffffff',
      border: '1px solid #e0e0e0',
      borderRadius: 12,
      padding: '10px 4px',
      minWidth: 200,
      maxWidth: '100%',
      boxSizing: 'border-box',
    }}>
      <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
        {stats.map((stat, idx) => (
          <div
            key={stat.key}
            style={{
              flex: 1,
              padding: '2px clamp(10px, 4vw, 18px)',
              textAlign: 'center',
              borderLeft: idx > 0 ? '1px solid #e8e8e8' : 'none',
              minWidth: 0,
            }}
          >
            <div style={{
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
              fontSize: 20,
              fontWeight: 700,
              color: '#0f084b',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}>
              {stat.value}
            </div>
            <div style={{
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
              fontSize: 12,
              color: '#666666',
              marginTop: 2,
            }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>
      {responseTimeLabel && (
        <div style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid #e8e8e8',
          textAlign: 'center',
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          color: responseTimeLabel === 'Responds quickly' ? '#58C27D' : '#333333',
        }}>
          {responseTimeLabel === 'Responds quickly' ? '⚡ ' : ''}{responseTimeLabel}
        </div>
      )}
    </div>
  );
};

const inputStyle = {
  border: 'none',
  borderBottom: '2px solid rgba(0,0,0,.12)',
  background: 'transparent',
  padding: '8px 0',
  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
  fontSize: '16px',
  color: '#0f084b',
  outline: 'none',
  width: '100%'
};

const labelStyle = {
  fontFamily: "'Sailors', serif",
  fontSize: '16px',
  color: '#0f084b',
  textAlign: 'left',
  marginTop: 6
};

const sectionTitleStyle = {
  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
  fontSize: 16,
  fontWeight: 800,
  color: '#0f084b',
  margin: '12px 0 -4px'
};

const AMENITIES = [
  'hot_tub','swimming_pool','sauna','cold_plunge','firepit','outdoor_deck',
  'lake','mountains','beach','golf','skiing','pet_friendly','family_friendly',
  'free_parking','ev_charger','grill','smartlock','low_toxin','accessible','river_access',
  'pickleball','tennis_courts'
];

const AMENITY_LABELS = {
  'hot_tub': 'Hot Tub',
  'swimming_pool': 'Swimming Pool',
  'sauna': 'Sauna',
  'cold_plunge': 'Cold Plunge',
  'firepit': 'Fire Pit',
  'outdoor_deck': 'Outdoor Deck',
  'lake': 'Lake Access',
  'mountains': 'Mountain Views',
  'beach': 'Beach Access',
  'golf': 'Golf Nearby',
  'skiing': 'Skiing Access',
  'pet_friendly': 'Pet Friendly',
  'family_friendly': 'Family Friendly',
  'free_parking': 'Free Parking',
  'ev_charger': 'EV Charger',
  'grill': 'Grill/BBQ',
  'smartlock': 'Smart Lock',
  'low_toxin': 'Low Toxin',
  'accessible': 'Accessible',
  'river_access': 'River Access',
  'pickleball': 'Access to Pickleball',
  'tennis_courts': 'Access to Tennis Courts'
};

const PROPERTY_TYPES = [
  'single_family_home','condo','townhome','tiny_home','cottage','guesthouse','cabin','a_frame'
];

const CATEGORIES = [
  { key: "travel", label: "Travel" },
  { key: "lifestyle", label: "Lifestyle" },
  { key: "parenting_family", label: "Parenting/Family" },
  { key: "food", label: "Food" },
  { key: "fashion_beauty", label: "Fashion/Beauty" },
  { key: "health_fitness", label: "Health & Fitness" },
  { key: "professional_photographer", label: "Professional Photographer" },
  { key: "drone_footage", label: "Drone Footage" },
  { key: "other", label: "Other" },
];

const PREFS = [
  { key: "paid_only", label: "Accepting Paid Partnerships Only" },
  { key: "gifted_and_paid", label: "Accepting Gifted & Paid Partnerships" },
  { key: "require_pet_friendly", label: "Require Pet Friendly Accommodations" },
  { key: "flexible_last_minute", label: "Flexible and willing to travel last minute" },
  { key: "require_advanced_notice", label: "Require advanced notice to travel" },
  { key: "willing_weekdays", label: "Willing to travel/stay on weekdays" },
  { key: "weekends_only", label: "Available on weekends only" },
];

const Chip = ({ text, onRemove }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 10px', borderRadius: 16, background: '#f0f0f0',
    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12, color: '#0f084b'
  }}>
    {text}
    <button type="button" onClick={onRemove} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>✕</button>
  </span>
);

const Profile = () => {
  const navigate = useNavigate();
  const { activePersona } = usePersona();
  const [emailParam, setEmailParam] = useState('');
  const [localEmail, setLocalEmail] = useState('');
  const [profileType, setProfileType] = useState(null); // 'creator' | 'host'
  const [creator, setCreator] = useState(null);
  const [host, setHost] = useState(null);
  const [showcase, setShowcase] = useState([]);
  const [propertyShowcase, setPropertyShowcase] = useState([]);
  const [hostCategory, setHostCategory] = useState(null);
  const [property, setProperty] = useState(null);
  const [properties, setProperties] = useState([]); // All properties for multi-property support
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false); // Start in read-only mode
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [authorized, setAuthorized] = useState(null);
  const [viewerMembershipTier, setViewerMembershipTier] = useState(null);
  const [emailLoaded, setEmailLoaded] = useState(false);
  
  // Scroll to the reviews section when arriving via a #reviews link (e.g. the
  // rating chip in directory modals). Reviews render after the async profile
  // fetch, so wait for loading to settle before looking for the anchor.
  useEffect(() => {
    if (loading) return;
    if (typeof window === 'undefined' || window.location.hash !== '#reviews') return;
    const timer = setTimeout(() => {
      const el = document.getElementById('reviews');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => clearTimeout(timer);
  }, [loading]);

  // Property management states
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isAddingProperty, setIsAddingProperty] = useState(false);

  // Editable form state (creator)
  const [creatorHandle, setCreatorHandle] = useState("");
  const [creatorCountry, setCreatorCountry] = useState("");
  const [creatorCity, setCreatorCity] = useState("");
  const [creatorState, setCreatorState] = useState("");
  const [igUrl, setIgUrl] = useState("");
  const [ttUrl, setTtUrl] = useState("");
  const [ytUrl, setYtUrl] = useState("");
  const [otherUrl, setOtherUrl] = useState("");
  const [creatorAvatarUrl, setCreatorAvatarUrl] = useState(null);
  const [selectedCats, setSelectedCats] = useState([]);
  const [nicheOther, setNicheOther] = useState("");
  const [creatorPartners, setCreatorPartners] = useState([]);
  const [creatorPartnerInput, setCreatorPartnerInput] = useState("");
  const [prefs, setPrefs] = useState([]);
  const [mediaKitUrl, setMediaKitUrl] = useState("");
  const [mediaKitFile, setMediaKitFile] = useState(null);
  const [mediaKitUploading, setMediaKitUploading] = useState(false);

  // Editable form state (host)
  const [brandName, setBrandName] = useState("");
  const [hostHandle, setHostHandle] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [hostCountry, setHostCountry] = useState("");
  const [hostCity, setHostCity] = useState("");
  const [hostState, setHostState] = useState("");
  const [linktree, setLinktree] = useState("");
  const [type, setType] = useState(PROPERTY_TYPES[0]);
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [sqft, setSqft] = useState("");
  const [nearestAirport, setNearestAirport] = useState("");
  const [airportMiles, setAirportMiles] = useState("");
  const [directUrl, setDirectUrl] = useState("");
  const [airbnbUrl, setAirbnbUrl] = useState("");
  const [vrboUrl, setVrboUrl] = useState("");
  const [otherListingUrl, setOtherListingUrl] = useState("");
  const [propertyImageUrl, setPropertyImageUrl] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [hostPartners, setHostPartners] = useState([]);
  const [hostPartnerInput, setHostPartnerInput] = useState("");
  const [collabText, setCollabText] = useState("");

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle country change for creator - reset state and city when country changes
  const handleCreatorCountryChange = (value) => {
    setCreatorCountry(value);
    if (value !== creatorCountry) {
      setCreatorState(""); // Reset state when country changes
      setCreatorCity(""); // Reset city when country changes
    }
  };

  // Handle state change for creator - reset city when state changes
  const handleCreatorStateChange = (value) => {
    setCreatorState(value);
    if (value !== creatorState) {
      setCreatorCity(""); // Reset city when state changes
    }
  };

  // Handle country change for host - reset state and city when country changes
  const handleHostCountryChange = (value) => {
    setHostCountry(value);
    if (value !== hostCountry) {
      setHostState(""); // Reset state when country changes
      setHostCity(""); // Reset city when country changes
    }
  };

  // Handle state change for host - reset city when state changes
  const handleHostStateChange = (value) => {
    setHostState(value);
    if (value !== hostState) {
      setHostCity(""); // Reset city when state changes
    }
  };

  const [handleParam, setHandleParam] = useState('');
  const [hostIdParam, setHostIdParam] = useState('');
  const [creatorIdParam, setCreatorIdParam] = useState('');
  const [propertyIdParam, setPropertyIdParam] = useState('');
  const [reviews, setReviews] = useState([]);
  const [creatorReviews, setCreatorReviews] = useState([]);
  const [hostReviews, setHostReviews] = useState([]);
  const [isFavorited, setIsFavorited] = useState(false);
  const [creatorEditOpen, setCreatorEditOpen] = useState(false);
  const [creatorEdit, setCreatorEdit] = useState({
    firstName: '',
    lastName: '',
    locationCity: '',
    locationState: '',
    avatarUrl: '',
    bio: '',
    ageRange: '',
    gender: ''
  });

  useEffect(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const pEmail = (params.get('email') || '').toLowerCase();
    const pHandle = params.get('handle') || '';
    const pHostId = params.get('host_id') || '';
    const pCreatorId = params.get('creator_id') || '';
    const pPropertyId = params.get('property_id') || '';
    setEmailParam(pEmail);
    setHandleParam(pHandle);
    setHostIdParam(pHostId);
    setCreatorIdParam(pCreatorId);
    setPropertyIdParam(pPropertyId);
    try {
      const e = (typeof window !== 'undefined' ? window.localStorage.getItem('postaway_email') || '' : '').toLowerCase();
      setLocalEmail(e);
    } catch (_) {}
    setEmailLoaded(true);
    // If URL contains edit=1, ensure edit mode is active
    const editParam = params.get('edit');
    if (editParam && editParam !== '0' && editParam !== 'false') setEditing(true);
    // Force creator view when coming from /creators
    const forceCreator = params.get('creator');
    if (forceCreator && forceCreator !== '0' && forceCreator !== 'false') {
      setProfileType('creator');
    }
    // Force host view when coming from /hosts
    const forceHost = params.get('host');
    if (forceHost && forceHost !== '0' && forceHost !== 'false') {
      setProfileType('host');
    }
  }, []);

  useEffect(() => {
    if (!emailLoaded) return; // wait until we've read localStorage
    const viewerEmail = localEmail;
    if (!viewerEmail) {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const res = await fetch('/api/access/subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: viewerEmail })
        });
        const data = await res.json();
        if (res.ok && data.ok && data.active) {
          setAuthorized(true);
          setViewerMembershipTier(data.membershipTier || 'all_access');
        } else {
          navigate('/');
        }
      } catch (_) {
        navigate('/');
      }
    })();
  }, [emailLoaded, localEmail, navigate]);

  const apiBase = useMemo(() => (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? 'http://localhost:4000' : '', []);

  const effectiveEmail = useMemo(() => emailParam || localEmail, [emailParam, localEmail]);
  const [profileEmail, setProfileEmail] = useState(null);
  const isSelf = useMemo(() => {
    // Check if it's the user's own profile
    if (handleParam && profileEmail) {
      // When viewing by handle, compare the profile's email with local email
      return profileEmail === localEmail;
    }
    // When viewing by creator_id or host_id, compare profile's email with local email
    if ((creatorIdParam || hostIdParam) && profileEmail) {
      return profileEmail === localEmail;
    }
    // When viewing by email (only if emailParam is actually set in URL)
    if (emailParam) {
      return effectiveEmail === localEmail;
    }
    // Default to false if we can't determine ownership
    return false;
  }, [effectiveEmail, localEmail, handleParam, profileEmail, creatorIdParam, hostIdParam, emailParam]);

  useEffect(() => {
    if (authorized !== true) return;
    if (!effectiveEmail && !handleParam && !hostIdParam && !creatorIdParam && !propertyIdParam) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        // Build query params separately for creator and host
        // When a specific ID is provided, ONLY query that API (IDs are type-specific)
        let creatorQueryParam = null;
        let hostQueryParam = null;
        
        if (creatorIdParam) {
          // Only query creator API when creator_id is present
          creatorQueryParam = `creator_id=${encodeURIComponent(creatorIdParam)}`;
        } else if (hostIdParam) {
          // Only query host API when host_id is present
          hostQueryParam = `host_id=${encodeURIComponent(hostIdParam)}`;
          if (propertyIdParam) {
            hostQueryParam += `&property_id=${encodeURIComponent(propertyIdParam)}`;
          }
        } else if (propertyIdParam) {
          // Only property_id - query host API (backend will find host by property ownership)
          hostQueryParam = `property_id=${encodeURIComponent(propertyIdParam)}`;
        } else {
          // No specific ID - query both APIs with handle or email
          const fallbackParam = handleParam ? 
            `handle=${encodeURIComponent(handleParam)}` : 
            `email=${encodeURIComponent(effectiveEmail)}`;
          
          creatorQueryParam = fallbackParam;
          hostQueryParam = fallbackParam;
          
          if (propertyIdParam) {
            hostQueryParam += `&property_id=${encodeURIComponent(propertyIdParam)}`;
          }
        }
        
        // Fetch from APIs - use dummy response if no param for that type
        const [cRes, hRes] = await Promise.all([
          creatorQueryParam 
            ? fetch(`${apiBase}/api/creator/profile?${creatorQueryParam}`)
            : Promise.resolve({ ok: false, json: async () => ({ ok: false }) }),
          hostQueryParam 
            ? fetch(`${apiBase}/api/host/profile?${hostQueryParam}`)
            : Promise.resolve({ ok: false, json: async () => ({ ok: false }) }),
        ]);
        const cJson = await cRes.json();
        const hJson = await hRes.json();
        const cProf = cRes.ok && cJson.ok ? (cJson.profile || null) : null;
        const hProf = hRes.ok && hJson.ok ? (hJson.profile || null) : null;
        const cShowcase = cRes.ok && cJson.ok && Array.isArray(cJson.showcase) ? cJson.showcase : [];
        const hShowcase = hRes.ok && hJson.ok && Array.isArray(hJson.showcase) ? hJson.showcase : [];
        const pShowcase = hRes.ok && hJson.ok && Array.isArray(hJson.property_showcase) ? hJson.property_showcase : [];
        setCreator(cProf);
        setHost(hProf);
        setShowcase(hProf ? hShowcase : cShowcase);
        setPropertyShowcase(pShowcase);
        setHostCategory(hProf?.host_category || null);
        // Keep local references to the freshly fetched property data: the
        // form-seeding block below runs in this same closure, so reading the
        // `property` state var here would see the STALE value (amenities and
        // property fields never hydrated, and saving wiped them — the
        // "disappearing amenities" bug).
        const fetchedProperty = hRes.ok && hJson.ok ? (hJson.property || null) : null;
        const fetchedProperties = hRes.ok && hJson.ok ? (hJson.properties || []) : [];
        setProperty(fetchedProperty);
        setProperties(fetchedProperties);
        setReviews(hRes.ok && hJson.ok ? (hJson.reviews || []) : []);
        
        const creatorReviewsData = cRes.ok && cJson.ok ? (cJson.reviews || []) : [];
        setCreatorReviews(creatorReviewsData);
        
        const hostReviewsData = hRes.ok && hJson.ok ? (hJson.hostReviews || []) : [];
        setHostReviews(hostReviewsData);
        
        // Set the profile's email for proper isSelf check.
        // When viewing self with no specific id/handle (i.e. the dropdown "Profile"
        // link), pick the profile that matches the active persona so creator/host
        // dual users see the side they're currently using. When a specific
        // creator_id / host_id was provided, that ID is type-specific so only one
        // of cProf/hProf will be set. When a handle was used, prefer whichever
        // side returned a non-null profile.
        const noSpecificParam = !creatorIdParam && !hostIdParam && !handleParam && !propertyIdParam;
        const isViewingSelf = noSpecificParam && (
          !effectiveEmail || effectiveEmail === localEmail
        );
        let pickHost;
        if (cProf && hProf) {
          if (isViewingSelf && activePersona) {
            pickHost = activePersona === 'host';
          } else {
            pickHost = true;
          }
        } else if (hProf) {
          pickHost = true;
        } else if (cProf) {
          pickHost = false;
        } else {
          pickHost = null;
        }

        if (pickHost === true) {
          setProfileType('host');
          setProfileEmail(hProf.email);
          if (handleParam && !emailParam) {
            setEmailParam(hProf.email);
          }
        } else if (pickHost === false) {
          setProfileType('creator');
          setProfileEmail(cProf.email);
          if (handleParam && !emailParam) {
            setEmailParam(cProf.email);
          }
        } else {
          setProfileType(null);
          setProfileEmail(null);
        }
        // Set favorite state from API
        const cFav = cRes.ok && cJson.ok ? (cJson.is_favorited || false) : false;
        const hFav = hRes.ok && hJson.ok ? (hJson.is_favorited || false) : false;
        setIsFavorited(hProf ? hFav : cFav);
        // seed form state
        if (cProf) {
          setCreatorHandle(cProf.handle || "");
          setCreatorCountry(cProf.location_country || "");
          setCreatorCity(cProf.location_city || "");
          setCreatorState(cProf.location_state || "");
          const socials = cProf.socials || {};
          setIgUrl((socials.instagram && (socials.instagram.url || socials.instagram)) || "");
          setTtUrl((socials.tiktok && (socials.tiktok.url || socials.tiktok)) || "");
          setYtUrl((socials.youtube && (socials.youtube.url || socials.youtube)) || "");
          setOtherUrl((socials.other && (socials.other.url || socials.other)) || "");
          setCreatorAvatarUrl(cProf.avatar_url || null);
          setSelectedCats(Array.isArray(cProf.niches) ? cProf.niches : []);
          setNicheOther(cProf.niche_other || "");
          setCreatorPartners(Array.isArray(cProf.partners) ? cProf.partners : []);
          setPrefs(Array.isArray(cProf.preferences) ? cProf.preferences : []);
          setMediaKitUrl(cProf.media_kit_url || "");
        }
        if (hProf) {
          setBrandName(hProf.brand_name || "");
          setHostHandle(hProf.handle || "");
          setLinktree(hProf.linktree_url || "");
          const seededPropertyImage =
            fetchedProperty?.main_image_url ||
            fetchedProperty?.image_url ||
            (Array.isArray(fetchedProperties) && fetchedProperties.find(p => p?.main_image_url)?.main_image_url) ||
            hProf.banner_url ||
            hProf.logo_url ||
            null;
          setPropertyImageUrl(seededPropertyImage);
          setHostPartners(Array.isArray(hProf.partners) ? hProf.partners : []);
          setCollabText(hProf.description || "");
          
          // Set owner name from host profile
          setOwnerName(hProf.owner_name || hProf.brand_name || "");
          
          if (fetchedProperty) {
            setHostCountry(fetchedProperty.location_country || "");
            setHostCity(fetchedProperty.location_city || "");
            setHostState(fetchedProperty.location_state || "");
            setType(fetchedProperty.property_type || "");
            setBedrooms(fetchedProperty.bedrooms != null ? String(fetchedProperty.bedrooms) : "");
            setBathrooms(fetchedProperty.bathrooms != null ? String(fetchedProperty.bathrooms) : "");
            setSqft(fetchedProperty.square_footage != null ? String(fetchedProperty.square_footage) : "");
            setNearestAirport(fetchedProperty.nearest_airport || "");
            setAirportMiles(fetchedProperty.driving_distance_miles != null ? String(fetchedProperty.driving_distance_miles) : "");
            setDirectUrl(fetchedProperty.direct_booking_url || "");
            setAirbnbUrl(fetchedProperty.airbnb_url || "");
            setVrboUrl(fetchedProperty.vrbo_url || "");
            setOtherListingUrl(fetchedProperty.other_listing_url || "");
            // Parse amenities if they come as PostgreSQL array string
            let propertyAmenities = fetchedProperty.amenities || [];
            if (typeof propertyAmenities === 'string') {
              if (propertyAmenities.startsWith('{') && propertyAmenities.endsWith('}')) {
                propertyAmenities = propertyAmenities.slice(1, -1).split(',').filter(a => a);
              } else {
                try {
                  propertyAmenities = JSON.parse(propertyAmenities);
                } catch (e) {
                  propertyAmenities = [];
                }
              }
            }
            setAmenities(Array.isArray(propertyAmenities) ? propertyAmenities : []);
          } else if (fetchedProperties && fetchedProperties.length > 0) {
            // If no specific property but we have properties, use the first one for basic info
            const firstProperty = fetchedProperties[0];
            setHostCountry(firstProperty.location_country || "");
            setHostCity(firstProperty.location_city || "");
            setHostState(firstProperty.location_state || "");
          }
        }
      } catch (e) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveEmail, handleParam, hostIdParam, creatorIdParam, propertyIdParam, authorized, activePersona]);

  // Track property views when a property is displayed (only for non-owners)
  useEffect(() => {
    if (property && property.id && profileType === 'host' && !isSelf) {
      // Track view (fire and forget, don't block UI)
      const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      fetch(`${apiBase}/api/properties/track-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: property.id })
      }).catch(err => console.error('Failed to track property view:', err));
    }
  }, [property?.id, profileType, isSelf]);

  // Track creator profile views when a creator profile is displayed (only for non-owners)
  useEffect(() => {
    if (creator && creator.profile_id && profileType === 'creator' && !isSelf) {
      // Track view (fire and forget, don't block UI)
      const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      fetch(`${apiBase}/api/creator/track-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_profile_id: creator.profile_id })
      }).catch(err => console.error('Failed to track creator profile view:', err));
    }
  }, [creator?.profile_id, profileType, isSelf]);

  const handleCatToggle = (key) => {
    setSelectedCats((prev) => {
      const exists = prev.includes(key);
      if (exists) {
        // Allow removing selected categories
        return prev.filter((k) => k !== key);
      } else {
        // Only allow adding if less than 3 are selected
        if (prev.length >= 3) {
          return prev; // Don't change if limit reached
        }
        return [...prev, key];
      }
    });
  };

  const handlePrefToggle = (key) => {
    setPrefs((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const toggleAmenity = (key) => {
    setAmenities((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const addCreatorPartnerChip = () => {
    const v = creatorPartnerInput.trim();
    if (!v) return;
    setCreatorPartners((p) => [...p, v]);
    setCreatorPartnerInput("");
  };

  const addHostPartnerChip = () => {
    const v = hostPartnerInput.trim();
    if (!v) return;
    setHostPartners((p) => (p.length >= 3 ? p : [...p, v]));
    setHostPartnerInput("");
  };

  const handleSave = async () => {
    if (!isSelf || !effectiveEmail) return;
    try {
      if (profileType === 'creator') {
        const socials = {
          instagram: igUrl || null,
          tiktok: ttUrl || null,
          youtube: ytUrl || null,
          other: otherUrl || null,
        };
        const res = await fetch(`${apiBase}/api/creator/profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: effectiveEmail,
            firstName: null,
            lastName: null,
            handle: creatorHandle || null,
            locationCountry: creatorCountry || null,
            locationCity: creatorCity || null,
            locationState: creatorState || null,
            socials,
            categories: selectedCats,
            nicheOther: selectedCats.includes('other') ? nicheOther : null,
            partners: creatorPartners,
            preferences: prefs,
            avatarUrl: creatorAvatarUrl || null,
            mediaKitUrl: mediaKitUrl || null,
          })
        });
        if (!res.ok) throw new Error('save_failed');
        // Navigate to collab-board after successful save
        navigate('/collab-board');
        return;
      } else if (profileType === 'host') {
        const propertyPayload = {
          name: (property && property.name) || brandName || null,
          type: type || null,
          location_country: hostCountry || null,
          location_city: hostCity || null,
          location_state: hostState || null,
          bedrooms: bedrooms ? Number(bedrooms) : null,
          bathrooms: bathrooms ? Number(bathrooms) : null,
          square_footage: sqft ? Number(sqft) : null,
          nearest_airport: nearestAirport || null,
          driving_distance_miles: airportMiles ? Number(airportMiles) : null,
          listings: {
            direct_booking_url: directUrl || null,
            airbnb_url: airbnbUrl || null,
            vrbo_url: vrboUrl || null,
            other_listing_url: otherListingUrl || null,
          },
          amenities,
          socials: (property && property.socials) || null,
          image_url: propertyImageUrl || null,
        };
        const res = await fetch(`${apiBase}/api/host/profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: effectiveEmail,
            brandName: brandName || null,
            handle: hostHandle || null,
            locationCountry: hostCountry || null,
            locationCity: hostCity || null,
            locationState: hostState || null,
            property: propertyPayload,
            linktree: linktree || null,
            partners: hostPartners,
            description: collabText || null,
          })
        });
        if (!res.ok) throw new Error('save_failed');
        // Navigate to collab-board after successful save
        navigate('/collab-board');
        return;
      }
      // If no specific profile type branch matched, still redirect
      navigate('/collab-board');
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert('There was an error saving your profile. Please try again.');
    }
  };

  const belowHeader = useMemo(() => {
    if (loading) return <div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>Loading profile…</div>;
    if (!profileType) return <div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>No profile found.</div>;
    return null;
  }, [loading, profileType]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    try {
      const url = await uploadFile(file);
      setCreatorAvatarUrl(url);
    } catch (_) {
      // eslint-disable-next-line no-alert
      alert('Failed to upload avatar. Please try again.');
    }
  };

  const handlePropertyImgChange = async (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    try {
      const url = await uploadFile(file);
      setPropertyImageUrl(url);
    } catch (_) {
      // eslint-disable-next-line no-alert
      alert('Failed to upload image. Please try again.');
    }
  };

  const handleMediaKitFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate PDF
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file');
      return;
    }
    
    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      alert('PDF file size must be less than 50MB');
      return;
    }
    
    setMediaKitUploading(true);
    try {
      const url = await uploadFile(file);
      setMediaKitUrl(url); // Set the URL from uploaded file
      setMediaKitFile(null);
    } catch (error) {
      alert('Failed to upload media kit PDF. Please try again.');
    } finally {
      setMediaKitUploading(false);
    }
  };

  // Inline edit controls (right column)
  // Property management functions
  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      const res = await fetch(`${apiBase}/api/host/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: effectiveEmail,
          operation: 'delete_property',
          property_id: propertyId
        })
      });
      
      if (!res.ok) throw new Error('delete_failed');
      
      // Remove from local state
      setProperties(prev => prev.filter(p => p.id !== propertyId));
      
      // If this was the current property, clear it
      if (property?.id === propertyId) {
        setProperty(null);
      }
    } catch (e) {
      alert('Failed to delete property. Please try again.');
    }
  };
  
  const handleSaveProperty = async (formData) => {
    try {
      const operation = editingProperty?.id ? 'update_property' : 'add_property';
      
      const res = await fetch(`${apiBase}/api/host/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: effectiveEmail,
          operation,
          property_id: editingProperty?.id,
          property: {
            ...formData,
            collab_opportunity: formData.description || formData.collab_opportunity
          }
        })
      });
      
      if (!res.ok) throw new Error('save_failed');
      
      // Refresh properties
      const refreshRes = await fetch(`${apiBase}/api/host/profile?email=${encodeURIComponent(effectiveEmail)}`);
      const refreshJson = await refreshRes.json();
      if (refreshRes.ok && refreshJson.ok) {
        setProperties(refreshJson.properties || []);
        if (!editingProperty && refreshJson.properties?.length > 0) {
          // If adding new property, set it as current
          setProperty(refreshJson.properties[0]);
        }
      }
      
      setShowPropertyModal(false);
      setEditingProperty(null);
    } catch (e) {
      alert('Failed to save property. Please try again.');
    }
  };

  const InlineEditControls = () => {
    if (!isSelf || !profileType) return null;
    if (!editing) return (
      <div style={{ margin: '0 0 8px', display: 'flex', justifyContent: 'flex-start' }}>
        <button type="button" onClick={() => setEditing(true)} style={{ background: '#0f084b', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 14 }}>Edit Profile</button>
      </div>
    );
    return (
      <div style={{ margin: '0 0 8px', display: 'flex', gap: 8 }}>
        <button type="button" onClick={handleSave} style={{ background: '#dc1d08', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 14 }}>Save Profile</button>
        <button type="button" onClick={() => setEditing(false)} style={{ background: 'transparent', color: '#0f084b', border: '2px solid #0f084b', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 14 }}>Cancel</button>
      </div>
    );
  };
  
  // Property Card Component
  const PropertyCard = ({ property, onEdit, onDelete }) => (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        height: 160,
        background: '#e0e0e0',
        backgroundImage: property.banner_url ? `url(${property.banner_url})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} />
      
      <div style={{ padding: 16 }}>
        <h4 style={{
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
          fontSize: 18,
          fontWeight: 600,
          color: '#0f084b',
          marginTop: 0,
          marginBottom: 8
        }}>
          {property.name || 'Unnamed Property'}
        </h4>
        
        <p style={{
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
          fontSize: 14,
          color: '#666',
          marginBottom: 16,
          margin: 0
        }}>
          {(() => {
            const parts = [];
            if (property.location_city) parts.push(property.location_city);
            if (property.location_state) parts.push(property.location_state);
            if (property.location_country && property.location_country !== 'United States') {
              parts.push(property.location_country);
            }
            return parts.join(', ') || 'Location not specified';
          })()}
        </p>
        
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onEdit}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: '1px solid #0f084b',
              borderRadius: 4,
              background: 'transparent',
              color: '#0f084b',
              cursor: 'pointer',
              fontSize: 14,
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
            }}
          >
            Edit
          </button>
          
          <button
            onClick={onDelete}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: '1px solid #dc1d08',
              borderRadius: 4,
              background: 'transparent',
              color: '#dc1d08',
              cursor: 'pointer',
              fontSize: 14,
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const handleProfileFavorite = async () => {
    const payload = profileType === 'creator' && creator?.profile_id
      ? { creatorProfileId: creator.profile_id }
      : property?.id
        ? { propertyId: property.id }
        : null;
    if (!payload) return;
    const result = await toggleFavorite(payload);
    if (result !== null) setIsFavorited(result);
  };

  const belowContent = useMemo(() => {
    if (error) return <div style={{ textAlign: 'center', color: '#0f084b' }}>{error}</div>;
    
    // Show unified profile view for both hosts and creators
    // This is the main non-editing view
    if (!creator || profileType === 'host' || profileType === 'creator') {
      // Use real data if available, otherwise use demo content
      const displayProperty = property || properties?.[0] || {};
      const creatorDisplayName = creator?.first_name || (creator?.handle ? `@${cleanHandle(creator.handle)}` : '');
      
      // Fix fallback text for creators vs hosts
      const propertyName = displayProperty.name 
        || brandName 
        || host?.brand_name 
        || creatorDisplayName 
        || (profileType === 'creator' ? (creator?.handle ? `@${cleanHandle(creator.handle)}` : 'Creator Profile') : 'Property');
      
      const locationDisplay = [
        displayProperty.location_city || hostCity || creatorCity,
        displayProperty.location_state || hostState || creatorState,
        (displayProperty.location_country || (profileType === 'host' ? hostCountry : creatorCountry)) && 
        (displayProperty.location_country || (profileType === 'host' ? hostCountry : creatorCountry)) !== 'United States' 
          ? (displayProperty.location_country || (profileType === 'host' ? hostCountry : creatorCountry)) 
          : null
      ].filter(Boolean).join(', ') || (profileType === 'creator' ? '' : 'Location not specified');
      const showcaseFallbackImage =
        (Array.isArray(propertyShowcase) && propertyShowcase.find(a => a?.image_url || a?.thumbnail_url)?.image_url) ||
        (Array.isArray(propertyShowcase) && propertyShowcase.find(a => a?.image_url || a?.thumbnail_url)?.thumbnail_url) ||
        (Array.isArray(showcase) && showcase.find(a => a?.image_url || a?.thumbnail_url)?.image_url) ||
        (Array.isArray(showcase) && showcase.find(a => a?.image_url || a?.thumbnail_url)?.thumbnail_url) ||
        null;
      const anyPropertyImage =
        displayProperty.main_image_url ||
        displayProperty.image_url ||
        (Array.isArray(properties) && properties.find(p => p?.main_image_url || p?.image_url)?.main_image_url) ||
        (Array.isArray(properties) && properties.find(p => p?.main_image_url || p?.image_url)?.image_url) ||
        null;
      const propertyImage =
        anyPropertyImage ||
        propertyImageUrl ||
        host?.banner_url ||
        host?.logo_url ||
        creatorAvatarUrl ||
        showcaseFallbackImage ||
        displayProperty.intro_video_poster_url ||
        host?.intro_video_poster_url ||
        creator?.intro_video_poster_url ||
        "/house.png";
      
      // Parse niches for creators
      let creatorNiches = [];
      let categoriesDisplay = [];
      if (profileType === 'creator' && creator?.niches) {
        creatorNiches = creator.niches || [];
        if (typeof creatorNiches === 'string') {
          if (creatorNiches.startsWith('{') && creatorNiches.endsWith('}')) {
            // PostgreSQL array format: {item1,item2,item3}
            creatorNiches = creatorNiches.slice(1, -1).split(',').map(n => n.trim().replace(/^"|"$/g, '')).filter(n => n);
          } else {
            try {
              creatorNiches = JSON.parse(creatorNiches);
            } catch (e) {
              creatorNiches = [];
            }
          }
        }
        if (Array.isArray(creatorNiches)) {
          categoriesDisplay = creatorNiches.map(c => {
            if (c === 'other' && creator.niche_other) {
              return creator.niche_other;
            }
            return CATEGORIES.find(cat => cat.key === c)?.label || c;
          }).filter(c => c);
        }
      }
      
      // Parse preferences for creators
      let creatorPreferences = [];
      let prefsDisplay = [];
      if (profileType === 'creator' && creator?.preferences) {
        creatorPreferences = creator.preferences || [];
        if (typeof creatorPreferences === 'string') {
          if (creatorPreferences.startsWith('{') && creatorPreferences.endsWith('}')) {
            // PostgreSQL array format: {item1,item2,item3}
            creatorPreferences = creatorPreferences.slice(1, -1).split(',').map(p => p.trim().replace(/^"|"$/g, '')).filter(p => p);
          } else {
            try {
              creatorPreferences = JSON.parse(creatorPreferences);
            } catch (e) {
              creatorPreferences = [];
            }
          }
        }
        if (Array.isArray(creatorPreferences)) {
          prefsDisplay = creatorPreferences.map(p => {
            const prefKey = typeof p === 'string' ? p.trim() : p;
            return PREFS.find(pref => pref.key === prefKey)?.label || prefKey;
          }).filter(p => p);
        }
      }
      
      // Parse partners for creators (similar to niches and preferences)
      let partnersDisplay = [];
      if (profileType === 'creator' && creator?.partners) {
        let creatorPartnersParsed = creator.partners || [];
        if (typeof creatorPartnersParsed === 'string') {
          if (creatorPartnersParsed.startsWith('{') && creatorPartnersParsed.endsWith('}')) {
            // PostgreSQL array format: {item1,item2,item3} or {@chestnuthillcabin}
            creatorPartnersParsed = creatorPartnersParsed
              .slice(1, -1) // Remove { and }
              .split(',')
              .map(p => p.trim().replace(/^"|"$/g, '')) // Remove quotes if present
              .filter(p => p && p.length > 0); // Filter out empty strings
          } else {
            try {
              // Try JSON parse as fallback
              creatorPartnersParsed = JSON.parse(creatorPartnersParsed);
            } catch (e) {
              creatorPartnersParsed = [];
            }
          }
        }
        // Ensure we have an array
        partnersDisplay = Array.isArray(creatorPartnersParsed) && creatorPartnersParsed.length > 0 
          ? creatorPartnersParsed 
          : (Array.isArray(creatorPartners) && creatorPartners.length > 0 ? creatorPartners : []);
      }
      
      const trustProfile = profileType === 'creator' ? creator : host;
      const completedCollabsCount = parseInt(trustProfile?.completed_collabs, 10) || 0;

      return (
        <div className="profile-page">
          <h1 className="profile-title">
            {propertyName}
            <CollabBadge
              completedCollabs={completedCollabsCount}
              isFounder={trustProfile?.founder}
              ratingDistribution={trustProfile?.rating_distribution}
              variant="stamp-inline"
            />
            {!isSelf && (
              <FavoriteInline
                isFavorited={isFavorited}
                onClick={handleProfileFavorite}
                size={20}
              />
            )}
          </h1>
          {/* Show property slug if viewing a specific property, otherwise show profile handle */}
          {!(viewerMembershipTier === 'basic' && !isSelf) && (
            propertyIdParam ? (
              property?.slug ? (
                <p className="profile-handle">
                  @{cleanHandle(property.slug)}
                </p>
              ) : null
            ) : (
              ((profileType === 'creator' && (creator?.handle || creatorHandle)) || 
               (profileType === 'host' && (host?.handle || hostHandle))) && (
                <p className="profile-handle">
                  @{cleanHandle(profileType === 'creator' 
                    ? (creator?.handle || creatorHandle)
                    : (host?.handle || hostHandle))}
                </p>
              )
            )
          )}
          <p className="profile-location">
            {locationDisplay}
          </p>

          {/* Trust signals (PRD 4/5): collab count, rating, reviews, response time */}
          <ProfileStatBlock
            completedCollabs={completedCollabsCount}
            averageRating={trustProfile?.average_rating}
            totalReviews={trustProfile?.total_reviews}
            responseTimeLabel={profileType === 'host' ? host?.response_time_label : null}
          />

          {/* Creator Social Links (navy icons) — hidden for basic viewers */}
          {profileType === 'creator' && !(viewerMembershipTier === 'basic' && !isSelf) && (() => {
            // Safely parse socials from DB (jsonb may arrive as string)
            let socialsFromDb = creator?.socials || {};
            if (typeof socialsFromDb === 'string') {
              try { socialsFromDb = JSON.parse(socialsFromDb || '{}'); } catch (_) { socialsFromDb = {}; }
            }
            const instagramUrl = igUrl || socialsFromDb.instagram?.url || socialsFromDb.instagram || '';
            const tiktokUrl = ttUrl || socialsFromDb.tiktok?.url || socialsFromDb.tiktok || '';
            const youtubeUrl = ytUrl || socialsFromDb.youtube?.url || socialsFromDb.youtube || '';
            const otherSocialUrl = otherUrl || socialsFromDb.other?.url || socialsFromDb.other || socialsFromDb.website || creator?.website_url || '';
            const anySocial = instagramUrl || tiktokUrl || youtubeUrl || otherSocialUrl;
            if (!anySocial) return null;
            return (
              <div className="profile-social-links">
                {instagramUrl && (
                  <a href={instagramUrl} target="_blank" rel="noopener noreferrer" onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.06)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    <svg width="22" height="22" viewBox="0 0 48 48" fill="#0f084b">
                      <path d="M36,4H12c-4.4,0-8,3.6-8,8v24c0,4.4,3.6,8,8,8h24c4.4,0,8-3.6,8-8V12C44,7.6,40.4,4,36,4z M24,34c-5.5,0-10-4.5-10-10s4.5-10,10-10s10,4.5,10,10S29.5,34,24,34z M35,15c-1.1,0-2-0.9-2-2c0-1.1,0.9-2,2-2s2,0.9,2,2C37,14.1,36.1,15,35,15z"/>
                    </svg>
                  </a>
                )}
                {tiktokUrl && (
                  <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.06)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    <svg width="22" height="22" viewBox="0 0 32 32" fill="#0f084b">
                      <path d="M16.656 1.029c1.637-0.025 3.262-0.012 4.886-0.025 0.054 2.031 0.878 3.859 2.189 5.213l-0.002-0.002c1.411 1.271 3.247 2.095 5.271 2.235l0.028 0.002v5.036c-1.912-0.048-3.71-0.489-5.331-1.247l0.082 0.034c-0.784-0.377-1.447-0.764-2.077-1.196l0.052 0.034c-0.012 3.649 0.012 7.298-0.025 10.934-0.103 1.853-0.719 3.543-1.707 4.954l0.020-0.031c-1.652 2.366-4.328 3.919-7.371 4.011l-0.014 0c-0.123 0.006-0.268 0.009-0.414 0.009-1.73 0-3.347-0.482-4.725-1.319l0.040 0.023c-2.508-1.509-4.238-4.091-4.558-7.094l-0.004-0.041c-0.025-0.625-0.037-1.25-0.012-1.862 0.49-4.779 4.494-8.476 9.361-8.476 0.547 0 1.083 0.047 1.604 0.136l-0.056-0.008c0.025 1.849-0.050 3.699-0.050 5.548-0.423-0.153-0.911-0.242-1.42-0.242-1.868 0-3.457 1.194-4.045 2.861l-0.009 0.030c-0.133 0.427-0.21 0.918-0.21 1.426 0 0.206 0.013 0.41 0.037 0.61l-0.002-0.024c0.332 2.046 2.086 3.59 4.201 3.59 0.061 0 0.121-0.001 0.181-0.004l-0.009 0c1.463-0.044 2.733-0.831 3.451-1.994l0.010-0.018c0.267-0.372 0.45-0.822 0.511-1.311l0.001-0.014c0.125-2.237 0.075-4.461 0.087-6.698 0.012-5.036-0.012-10.060 0.025-15.083z"/>
                    </svg>
                  </a>
                )}
                {youtubeUrl && (
                  <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.06)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    <svg width="22" height="22" viewBox="0 0 20 14" fill="#0f084b">
                      <path d="M19.615 3.154c-.19-.84-.75-1.5-1.577-1.692C16.264 1.1 10 .9 10 .9S3.736 1.1 2.962 1.462c-.827.192-1.387.852-1.577 1.692C1.1 5.03 1 6.5 1 6.5s.1 1.47.385 3.346c.19.84.75 1.5 1.577 1.692C3.736 11.9 10 12.1 10 12.1s6.264-.2 7.038-.562c.827-.192 1.387-.852 1.577-1.692C18.9 7.97 19 6.5 19 6.5s-.1-1.47-.385-3.346zM8.182 9.182V3.818L13.182 6.5l-5 2.682z"/>
                    </svg>
                  </a>
                )}
                {otherSocialUrl && (
                  <a href={otherSocialUrl} target="_blank" rel="noopener noreferrer" onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.06)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f084b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 17H7A5 5 0 0 1 7 7h2"/>
                      <path d="M15 7h2a5 5 0 1 1 0 10h-2"/>
                      <line x1="8" x2="16" y1="12" y2="12"/>
                    </svg>
                  </a>
                )}
              </div>
            );
          })()}
          <div className="profile-image-container" onMouseEnter={(e)=>{ if(isSelf && profileType==='creator'){ const btn=e.currentTarget.querySelector('#editBtn'); if(btn) btn.style.opacity='1'; }}} onMouseLeave={(e)=>{ if(isSelf && profileType==='creator'){ const btn=e.currentTarget.querySelector('#editBtn'); if(btn) btn.style.opacity='0'; }}}>
            {(profileType === 'creator' ? creator?.intro_video_url : host?.intro_video_url) ? (
              <IntroVideoPreview
                mode="hero"
                posterUrl={(profileType === 'creator' ? creator?.intro_video_poster_url : host?.intro_video_poster_url) || propertyImage}
                publicId={profileType === 'creator' ? creator?.intro_video_public_id : host?.intro_video_public_id}
                fullUrl={profileType === 'creator' ? creator?.intro_video_url : host?.intro_video_url}
                fallbackImageUrl={propertyImage}
                alt={profileType === 'creator' ? 'Creator intro video' : 'Host intro video'}
              />
            ) : (
              <img 
                src={propertyImage}
                alt="Property" 
                onError={(e) => handleCloudinaryImageError(e, '/house.png')}
              />
            )}
            {isSelf && profileType === 'creator' && (
              <button id="editBtn" className="profile-edit-button" onClick={()=>{
                setCreatorEdit({
                  firstName: creator?.first_name || '',
                  lastName: creator?.last_name || '',
                  locationCity: creatorCity || creator?.location_city || '',
                  locationState: creatorState || creator?.location_state || '',
                  avatarUrl: creatorAvatarUrl || creator?.avatar_url || '',
                  bio: creator?.bio || '',
                  ageRange: creator?.age_range || '',
                  gender: creator?.gender || ''
                });
                setCreatorEditOpen(true);
              }} title="Edit profile">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"/>
                  <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z"/>
                </svg>
              </button>
            )}
          </div>
          {(() => {
            // When viewing a specific property, prefer the property-level showcase.
            // Fall back to the host's profile-level showcase if the property has
            // no assets yet (keeps the page from feeling empty during the rollout).
            const hasPropertyShowcase = Array.isArray(propertyShowcase) && propertyShowcase.length > 0;
            const hasHostShowcase = Array.isArray(showcase) && showcase.length > 0;
            if (property?.id && hasPropertyShowcase) {
              return <ShowcaseGrid assets={propertyShowcase} heading="Property showcase" />;
            }
            if (hasHostShowcase) {
              return <ShowcaseGrid assets={showcase} />;
            }
            return null;
          })()}
          {profileType !== 'creator' && (
            <div className="profile-property-badges">
              <div>
                {displayProperty.bedrooms || bedrooms || 2} Bedroom{(displayProperty.bedrooms || bedrooms || 2) !== 1 ? 's' : ''}
              </div>
              <div>
                {displayProperty.bathrooms || bathrooms || 1} Bath{(displayProperty.bathrooms || bathrooms || 1) !== 1 ? 's' : ''}
              </div>
              <div>
                {displayProperty.max_guests ? `${displayProperty.max_guests} Guests` : 'Entire Home'}
              </div>
            </div>
          )}
          {/* {profileType !== 'creator' && (
          <div className="profile-host-info">
            <div className="host-avatar">
              <img 
                src={(() => {
                  // Try multiple image sources in order of preference
                  if (host?.logo_url) return host.logo_url;
                  if (host?.banner_url) return host.banner_url;
                  if (propertyImageUrl) return propertyImageUrl;
                  if (displayProperty?.main_image_url) return displayProperty.main_image_url;
                  // Generate avatar based on name if no image available
                  const name = displayProperty.property_owner_name || host?.owner_name || ownerName || brandName || 'Host';
                  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=0f084b&color=fff&size=40`;
                })()}
                alt="Host" 
              />
            </div>
            <div className="host-name">
              Hosted by {displayProperty.property_owner_name || host?.owner_name || ownerName || brandName || 'Host'}
            </div>
            <div className="host-rating">
              {[1,2,3,4,5].map(i => (
                <span key={i} style={{ 
                  color: i <= Math.floor(displayProperty.average_rating || 5) ? '#dc1d08' : '#e0e0e0'
                }}>★</span>
              ))}
            </div>
          </div>
          )} */}
          
          {/* Booking Links - Centered below Hosted by */}
          {profileType === 'host' && (displayProperty.airbnb_url || displayProperty.vrbo_url || displayProperty.direct_booking_url) && (
            <div className="profile-booking-links">
              {displayProperty.direct_booking_url && (
                <Tippy content="Book directly with the host and save">
                <a
                  href={displayProperty.direct_booking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <img src="/circle-p-logo.png" alt="Direct" />
                </a>
                </Tippy>
              )}
              {displayProperty.vrbo_url && (
                <a
                  href={displayProperty.vrbo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <img src="/vrbo logo.png" alt="VRBO" />
                </a>
              )}
              {displayProperty.airbnb_url && (
                <a
                  href={displayProperty.airbnb_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <img src="/airbnb_logo.png" alt="Airbnb" />
                </a>
              )}
            </div>
          )}
          
          {/* Niches/Categories - Only for creators */}
          {profileType === 'creator' && categoriesDisplay.length > 0 && (
            <div className="profile-categories">
              <div className="categories-list">
                {categoriesDisplay.map((cat, i) => (
                  <span key={i}>
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* About Section - Show creator bio or property description if available */}
          {((profileType === 'creator' && creator?.bio) || 
            (profileType === 'host' && (displayProperty.description || host?.description))) && (
            <div className="profile-about">
              <h3 style={{ textAlign: 'center' }}>
                About
              </h3>
              <p>
                {profileType === 'creator' ? creator?.bio : (displayProperty.description || host?.description)}
              </p>
            </div>
          )}
          
          {/* Media Kit - Only for creators */}
          {profileType === 'creator' && (mediaKitUrl || creator?.media_kit_url) && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '24px',
              marginBottom: '8px'
            }}>
              <a
                href={mediaKitUrl || creator?.media_kit_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: '#0f084b',
                  color: '#ffffff',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '15px',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" x2="8" y1="13" y2="13"/>
                  <line x1="16" x2="8" y1="17" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
                Download Media Kit
              </a>
            </div>
          )}
          
          {/* Preferences - Only for creators */}
          {profileType === 'creator' && prefsDisplay.length > 0 && (
            <div className="profile-preferences">
              <div className="preferences-list">
                {prefsDisplay.map((pref, i) => (
                  <span key={i}>
                    {pref}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          
          {/* Self-reported partner tags — retitled "Also worked with" so
              they can't be mistaken for verified Postaway collabs (PRD 4). */}
          {profileType === 'creator' && partnersDisplay.length > 0 && (
            <div className="profile-collaborations">
              <h3>
                Also Worked With
              </h3>
              <div className="collaborations-list">
                {partnersDisplay.map((partner, i) => (
                  <span 
                    key={i}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#dc1d08';
                      e.currentTarget.style.background = '#dc1d08';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#0f084b';
                      e.currentTarget.style.background = '#0f084b';
                    }}
                  >
                    {partner}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Nearest Airport - Only for hosts */}
          {profileType === 'host' && (() => {
            const airportCode = displayProperty.nearest_airport || nearestAirport || (properties && properties.length > 0 ? properties[0]?.nearest_airport : null);
            const airportDistance = displayProperty.driving_distance_miles != null ? displayProperty.driving_distance_miles : 
                                   (airportMiles != null && airportMiles !== '' ? airportMiles : 
                                   (properties && properties.length > 0 ? properties[0]?.driving_distance_miles : null));
            const airportTime = displayProperty.driving_time_minutes != null ? displayProperty.driving_time_minutes : 
                               (properties && properties.length > 0 ? properties[0]?.driving_time_minutes : null);
            
            return airportCode && airportCode.trim() !== '' ? (
              <div className="profile-about" style={{ marginBottom: '24px', marginTop: '32px' }}>
                <h3 style={{ textAlign: 'center' }}>
                  Getting There
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px 20px',
                  background: '#f8f8f8',
                  borderRadius: '12px',
                  marginTop: '12px'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#0f084b">
                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                  </svg>
                  <div>
                    <div style={{
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: '15px',
                      color: '#0f084b',
                      fontWeight: 600,
                      marginBottom: '2px'
                    }}>
                      {airportCode}
                    </div>
                    {(airportDistance != null && airportDistance !== '') || airportTime != null ? (
                      <div style={{
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                        fontSize: '13px',
                        color: '#666'
                      }}>
                        {airportDistance != null && airportDistance !== '' ? `${airportDistance} miles` : ''}
                        {airportDistance != null && airportDistance !== '' && airportTime != null ? ' • ' : ''}
                        {airportTime != null ? `${airportTime} min drive` : ''}
                        {airportDistance == null && airportTime == null ? 'away' : ''}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null;
          })()}
          
          {profileType !== 'creator' && (
          <div className="profile-amenities">
            <h3>
              Amenities
            </h3>
            <div className="amenities-grid">
              {(() => {
                let amenitiesList = displayProperty.amenities || amenities || [];
                // Handle case where amenities is a PostgreSQL array string from database
                if (typeof amenitiesList === 'string') {
                  // Check if it's a PostgreSQL array format like "{item1,item2,item3}"
                  if (amenitiesList.startsWith('{') && amenitiesList.endsWith('}')) {
                    // Remove the curly braces and split by comma
                    amenitiesList = amenitiesList.slice(1, -1).split(',').filter(a => a);
                  } else {
                    try {
                      // Try JSON parse as fallback
                      amenitiesList = JSON.parse(amenitiesList);
                    } catch (e) {
                      amenitiesList = [];
                    }
                  }
                }
                // Ensure it's an array
                if (!Array.isArray(amenitiesList)) {
                  amenitiesList = [];
                }
                // Filter out any null/undefined values and display with proper labels
                const validAmenities = amenitiesList.filter(a => a && AMENITY_LABELS[a]);
                
                if (validAmenities.length === 0) {
                  return <div style={{
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: '14px',
                    color: '#999',
                    fontStyle: 'italic'
                  }}>No amenities listed</div>;
                }
                
                return validAmenities.map((amenity, idx) => (
                <div key={idx} style={{
                  padding: '8px 12px',
                  background: '#0f084b',
                  borderRadius: '20px',
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '14px',
                  color: '#ffffff',
                  border: 'none',
                  marginBottom: '8px'
                }}>
                  {AMENITY_LABELS[amenity] || amenity}
                </div>
              ));
              })()}
            </div>

            {/* Book Here Button */}
            {(() => {
              // Priority: Direct -> Airbnb -> VRBO
              const bookUrl = displayProperty.direct_booking_url || displayProperty.airbnb_url || displayProperty.vrbo_url;
              
              if (bookUrl) {
                return (
                  <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <a 
                      href={bookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: '#dc1d08', // Red button
                        color: '#ffffff',
                        padding: '14px 40px',
                        borderRadius: '30px',
                        textDecoration: 'none',
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: '16px',
                        boxShadow: '0 4px 14px rgba(220, 29, 8, 0.3)',
                        transition: 'all 0.2s ease',
                        display: 'inline-block'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(220, 29, 8, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(220, 29, 8, 0.3)';
                      }}
                    >
                      Book Here
                    </a>
                  </div>
                );
              }
              return null;
            })()}
          </div>
          )}

          {/* Creator Reviews Section */}
          {profileType === 'creator' && creatorReviews && creatorReviews.length > 0 && (
            <div className="profile-reviews" id="reviews">
              <h3>
                Host Reviews {creator?.total_reviews ? `(${creator.total_reviews})` : ''}
              </h3>
              <div className="reviews-grid">
                {creatorReviews.map((review, index) => (
                  <ReviewCard
                    key={`creator-desktop-${index}`}
                    review={review}
                    reviewerName={review.host_name || 'Host'}
                    reviewerAvatar={review.host_avatar_url}
                    collaborationMonth={review.collaboration_month}
                    propertyName={review.property_name}
                    rating={review.rating}
                    reviewTitle={review.review_title}
                    reviewText={review.review_text}
                    response={review.creator_response}
                    responseLabel="Response from Creator"
                    index={index}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Host Reviews Section */}
          {profileType === 'host' && hostReviews && hostReviews.length > 0 && (
            <div className="profile-reviews" id="reviews">
              <h3>
                Creator Reviews {host?.total_reviews ? `(${host.total_reviews})` : ''}
              </h3>
              <div className="reviews-grid">
                {hostReviews.map((review, index) => (
                  <ReviewCard
                    key={`host-desktop-${index}`}
                    review={review}
                    reviewerName={review.creator_name || 'Creator'}
                    reviewerAvatar={review.creator_avatar_url}
                    collaborationMonth={review.collaboration_month}
                    propertyName={review.property_name}
                    rating={review.rating}
                    reviewTitle={review.review_title}
                    reviewText={review.review_text}
                    response={review.host_response}
                    responseLabel="Response from Host"
                    index={index}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    if (loading) return null;
    if (!profileType) return null;

    if (profileType === 'creator' && creator && editing) {

      // Editing: mirror CreatorOnboarding layout
      const header = (
        <>
          <h3 style={{
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
            fontSize: '20px',
            fontWeight: 800,
            color: '#0f084b',
            textAlign: 'center',
            // push header below the brand lockup in the postcard header
            margin: '48px 0 -8px'
          }}>Edit your creator profile</h3>
          <div style={{
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
            fontSize: 14,
            color: '#0f084b',
            textAlign: 'center',
            opacity: .9,
            marginBottom: 16
          }}>Update your info and save when you're done.</div>
        </>
      );

      return (
        <>
          {/* Left side - Image Upload */}
          <div style={{
            position: 'absolute', left: 0, right: '50%', top: '360px', bottom: '80px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 3, padding: '0 48px'
          }}>
            <label htmlFor="avatar" style={{
              maxWidth: 500, maxHeight: 600, border: '2px dashed rgba(0,0,0,.3)', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f084b', cursor: 'pointer', padding: '8px'
            }}>
              {creatorAvatarUrl ? (
                <img alt="avatar" src={creatorAvatarUrl} style={{ maxWidth: '100%', maxHeight: '580px', display: 'block', borderRadius: 8 }} />
              ) : (
                <div style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 16 }}>Upload your Profile Picture (jpeg or png)</div>
              )}
            </label>
            <input id="avatar" type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }} onChange={handleAvatarChange} />
            <p style={{
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 16, fontWeight: 500,
              color: '#0f084b', margin: '12px 0 0', textAlign: 'center'
            }}>{creatorHandle ? `@${cleanHandle(creatorHandle)}` : '@yourhandle'} x @yourfavoritecabin</p>
          </div>

          {/* Right side - Form */}
          <div style={{
            position: 'absolute', left: '50%', right: 0, top: '560px', bottom: '80px', display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 3, padding: '0 48px'
          }}>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%', maxWidth: '100%' }}>
              {header}
              <div>
                <input 
                  style={inputStyle} 
                  value={creatorHandle} 
                  onChange={(e) => setCreatorHandle(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))} 
                  placeholder="yourhandle" 
                />
                <label style={labelStyle}>Handle (username)</label>
              </div>
              <div>
                <LocationAutocomplete
                  type="country"
                  value={creatorCountry}
                  onChange={handleCreatorCountryChange}
                  placeholder="Start typing country..."
                  label="Country"
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <LocationAutocomplete
                    type="state"
                    value={creatorState}
                    onChange={handleCreatorStateChange}
                    placeholder={creatorCountry === "United States" ? "Start typing state..." : "State (optional)"}
                    label="State"
                    disabled={creatorCountry && creatorCountry !== "United States"}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <LocationAutocomplete
                    type="city"
                    value={creatorCity}
                    onChange={setCreatorCity}
                    stateValue={creatorState}
                    placeholder={creatorState ? "Start typing city..." : creatorCountry === "United States" ? "Select state first" : "Start typing city..."}
                    label="City"
                    disabled={creatorCountry === "United States" && !creatorState}
                  />
                </div>
              </div>
              <div>
                <input style={inputStyle} value={igUrl} onChange={(e) => setIgUrl(e.target.value)} placeholder="" />
                <label style={labelStyle}>Instagram URL</label>
              </div>
              <div>
                <input style={inputStyle} value={ttUrl} onChange={(e) => setTtUrl(e.target.value)} placeholder="" />
                <label style={labelStyle}>TikTok URL</label>
              </div>
              <div>
                <input style={inputStyle} value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} placeholder="" />
                <label style={labelStyle}>YouTube URL</label>
              </div>
              <div>
                <input style={inputStyle} value={otherUrl} onChange={(e) => setOtherUrl(e.target.value)} placeholder="" />
                <label style={labelStyle}>Other URL</label>
              </div>

              {/* Categories */}
              <div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6 }}>
                  {CATEGORIES.map((c) => {
                    const isSelected = selectedCats.includes(c.key);
                    const isDisabled = !isSelected && selectedCats.length >= 3;
                    return (
                      <button type="button" key={c.key} onClick={() => handleCatToggle(c.key)}
                        disabled={isDisabled}
                        style={{
                          borderRadius: 18, padding: '6px 12px', border: '1px solid rgba(0,0,0,.2)', 
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          background: isSelected ? '#0f084b' : 'transparent', 
                          color: isSelected ? '#fff' : (isDisabled ? '#ccc' : '#0f084b'),
                          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12,
                          opacity: isDisabled ? 0.5 : 1
                        }}>{c.label}</button>
                    );
                  })}
                </div>
                <label style={labelStyle}>Categories</label>
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {selectedCats.length}/3 selected
                </div>
                {selectedCats.includes('other') && (
                  <div style={{ marginTop: 8 }}>
                    <input
                      style={{ ...inputStyle, fontSize: 14 }}
                      value={nicheOther}
                      onChange={(e) => setNicheOther(e.target.value)}
                      placeholder="Please specify..."
                    />
                  </div>
                )}
              </div>

              {/* Partners chips */}
              <div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6, marginBottom: 8 }}>
                  {creatorPartners.map((p, i) => (
                    <Chip key={`${p}-${i}`} text={p} onRemove={() => setCreatorPartners((arr) => arr.filter((_, idx) => idx !== i))} />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inputStyle, flex: 1 }} value={creatorPartnerInput} onChange={(e) => setCreatorPartnerInput(e.target.value)} placeholder="Add partner" />
                  <button type="button" onClick={addCreatorPartnerChip} style={{
                    background: '#0f084b', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 14
                  }}>Add</button>
                </div>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Previous Collaboration Partners
                  <Tippy content="Enter any previous hosts, hotels or tourism boards you have collaborated with">
                    <span style={{ display: 'inline-flex', cursor: 'help' }}>
                      <Icon name="info" size={14} />
                    </span>
                  </Tippy>
                </label>
              </div>

              {/* Media Kit - PDF Upload or URL */}
              <div>
                <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                  {/* PDF Upload Option */}
                  <div>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleMediaKitFileChange}
                      disabled={mediaKitUploading}
                      style={{ display: 'none' }}
                      id="media-kit-upload"
                    />
                    <label
                      htmlFor="media-kit-upload"
                      style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        background: '#0f084b',
                        color: 'white',
                        borderRadius: 8,
                        cursor: mediaKitUploading ? 'not-allowed' : 'pointer',
                        fontSize: 14,
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                        opacity: mediaKitUploading ? 0.6 : 1
                      }}
                    >
                      {mediaKitUploading ? 'Uploading...' : 'Upload PDF Media Kit'}
                    </label>
                    {mediaKitUrl && (mediaKitUrl.endsWith('.pdf') || mediaKitUrl.includes('blob.vercel-storage.com')) && (
                      <a
                        href={mediaKitUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          marginLeft: '12px',
                          color: '#0f084b',
                          textDecoration: 'underline',
                          fontSize: 14
                        }}
                      >
                        View Current PDF
                      </a>
                    )}
                  </div>
                  
                  {/* OR divider */}
                  <div style={{ textAlign: 'center', color: '#666', fontSize: 12 }}>
                    OR
                  </div>
                  
                  {/* URL Input Option */}
                  <div>
                    <input 
                      style={inputStyle} 
                      value={mediaKitUrl} 
                      onChange={(e) => setMediaKitUrl(e.target.value)} 
                      placeholder="https://your-media-kit-link.com" 
                    />
                    <label style={labelStyle}>Media Kit URL</label>
                  </div>
                </div>
              </div>

              {/* Preferences multi-select */}
              <div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6 }}>
                  {PREFS.map((p) => (
                    <button type="button" key={p.key} onClick={() => handlePrefToggle(p.key)}
                      style={{
                        borderRadius: 18, padding: '6px 12px', border: '1px solid rgba(0,0,0,.2)', cursor: 'pointer',
                        background: prefs.includes(p.key) ? '#0f084b' : 'transparent', color: prefs.includes(p.key) ? '#fff' : '#0f084b',
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12,
                      }}>{p.label}</button>
                  ))}
                </div>
                <label style={labelStyle}>Collaboration Preference</label>
              </div>

              <button type="submit" style={{
                background: '#dc1d08', color: '#fff', border: 'none', borderRadius: 8,
                padding: '14px 32px', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 18, fontWeight: 800, letterSpacing: '0.08em',
                cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textTransform: 'uppercase'
              }}>Save Profile</button>
            </form>
          </div>
        </>
      );
    }

    if (profileType === 'host' && host) {
      const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
      // Use property data if available, otherwise fall back to first property or form state
      const displayProperty = property || (properties && properties.length > 0 ? properties[0] : {}) || {};
      const imageSrc = (displayProperty.main_image_url || propertyImageUrl || host.banner_url || '')
        ? ((isLocal && (displayProperty.main_image_url || propertyImageUrl || host.banner_url).startsWith('/')) 
          ? `http://localhost:4000${displayProperty.main_image_url || propertyImageUrl || host.banner_url}` 
          : (displayProperty.main_image_url || propertyImageUrl || host.banner_url))
        : '';

      // New modern host profile design - always read-only
      const locationDisplay = [
        displayProperty.location_city || hostCity || '', 
        displayProperty.location_state || hostState || '',
        (displayProperty.location_country || hostCountry) && 
        (displayProperty.location_country || hostCountry) !== 'United States' 
          ? (displayProperty.location_country || hostCountry) 
          : null
      ].filter(Boolean).join(', ');
      
      const typeDisplay = (displayProperty.property_type || type || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      // Parse amenities if they come as PostgreSQL array string
      let amenitiesArray = displayProperty.amenities || amenities || [];
      if (typeof amenitiesArray === 'string') {
        if (amenitiesArray.startsWith('{') && amenitiesArray.endsWith('}')) {
          amenitiesArray = amenitiesArray.slice(1, -1).split(',').filter(a => a);
        } else {
          try {
            amenitiesArray = JSON.parse(amenitiesArray);
          } catch (e) {
            amenitiesArray = [];
          }
        }
      }
      if (!Array.isArray(amenitiesArray)) {
        amenitiesArray = [];
      }
      
      const amenitiesFormatted = amenitiesArray
        .filter(a => a && AMENITY_LABELS[a])
        .map(a => AMENITY_LABELS[a] || a);

        return (
            <div style={{
          position: 'absolute',
          left: '5%',
          right: '5%', 
          top: '80px',
          bottom: '80px',
          display: 'flex',
          gap: '40px',
          padding: '40px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          zIndex: 3
        }}>
          {/* Left Column - Property Image & Quick Info */}
          <div style={{
            flex: '0 0 400px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {/* Property Image */}
            <div style={{
              width: '100%',
              maxHeight: '500px',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
            }}>
                {imageSrc ? (
                <img 
                  alt="Property" 
                  src={imageSrc} 
                  onError={(e) => handleCloudinaryImageError(e, '/house.png')}
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    display: 'block' 
                  }} 
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '72px',
                  color: 'white',
                  fontFamily: "'Sailors', serif"
                }}>
                  {(brandName || 'P')[0]}
                </div>
                )}
              </div>

            {/* Property Name & Handle */}
            <div style={{ textAlign: 'center' }}>
              <h1 style={{
                fontFamily: "'Sailors', serif",
                fontSize: '28px',
                color: '#0f084b',
                margin: '0 0 8px 0'
              }}>
                {displayProperty.name || brandName || property?.title || 'Property'}
              </h1>
              {hostHandle && (
                <p style={{
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '18px',
                  color: '#dc1d08',
                  margin: 0
                }}>
                  @{cleanHandle(hostHandle)}
                </p>
              )}
              {displayProperty.average_rating > 0 && (
                <div style={{
                  marginTop: '8px',
                  fontSize: '16px',
                  color: '#dc1d08'
                }}>
                  {'★'.repeat(Math.round(displayProperty.average_rating))}
                  {'☆'.repeat(5 - Math.round(displayProperty.average_rating))}
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '14px',
                    color: '#666',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
                  }}>
                    ({displayProperty.total_reviews} {displayProperty.total_reviews === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
              )}
              </div>

            {/* Location */}
            {locationDisplay && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: '#f8f8f8',
                borderRadius: '12px'
              }}>
                <svg width="16" height="16" fill="#0f084b">
                  <path d="M8 0C3.58 0 0 3.58 0 8c0 5.25 7 12 7.29 12.29a1 1 0 0 0 1.41 0C9 20 16 13.25 16 8c0-4.42-3.58-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                </svg>
                <span style={{
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '14px',
                  color: '#0f084b'
                }}>
                  {locationDisplay}
                </span>
            </div>
            )}

            {/* Quick Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px'
            }}>
              {(displayProperty.bedrooms || bedrooms) && (
                <div style={{
                  padding: '12px',
                  background: '#f8f8f8',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontFamily: "'Sailors', serif",
                    fontSize: '24px',
                    color: '#0f084b'
                  }}>
                    {displayProperty.bedrooms || bedrooms}
                </div>
                  <div style={{
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '4px'
                  }}>
                    Bedrooms
                </div>
                </div>
              )}
              {(displayProperty.bathrooms || bathrooms) && (
                <div style={{
                  padding: '12px',
                  background: '#f8f8f8',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontFamily: "'Sailors', serif",
                    fontSize: '24px',
                    color: '#0f084b'
                  }}>
                    {displayProperty.bathrooms || bathrooms}
                </div>
                  <div style={{
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '4px'
                  }}>
                    {displayProperty.bathrooms === 1 ? 'Bathroom' : 'Bathrooms'}
                </div>
                </div>
              )}
                </div>

            {/* Listing Links */}
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              {(displayProperty.airbnb_url || airbnbUrl) && (
                <a 
                  href={displayProperty.airbnb_url || airbnbUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: '#FF5A5F',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  title="View on Airbnb"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M12 18.8c-1.3-1.8-2.1-3.3-2.6-4.4-.4-.9-.6-1.7-.6-2.4 0-1.7 1.4-3.2 3.2-3.2s3.2 1.4 3.2 3.2c0 .7-.2 1.5-.6 2.4-.5 1.1-1.3 2.6-2.6 4.4zm0-11.3c-2.6 0-4.7 2.1-4.7 4.7 0 .9.3 1.8.7 2.9.5 1.3 1.4 2.8 2.8 4.8.3.4.8.7 1.2.7.5 0 .9-.2 1.2-.7 1.4-1.9 2.3-3.5 2.8-4.8.5-1.1.7-2 .7-2.9 0-2.6-2.1-4.7-4.7-4.7z"/>
                  </svg>
                </a>
              )}
              {(displayProperty.vrbo_url || vrboUrl) && (
                <a
                  href={displayProperty.vrbo_url || vrboUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: '#1e3a5f',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  title="View on Vrbo"
                >
                  <span style={{ color: 'white', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: '12px', fontWeight: 'bold' }}>VRBO</span>
                </a>
              )}
              {(displayProperty.direct_booking_url || directUrl) && (
                <a
                  href={displayProperty.direct_booking_url || directUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: '#0f084b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  title="Direct Booking"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/>
                  </svg>
                </a>
              )}
              {(displayProperty.other_listing_url || otherListingUrl) && (
                <a
                  href={displayProperty.other_listing_url || otherListingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  title="Other Listing"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                  </svg>
                </a>
              )}
                </div>
                </div>

          {/* Right Column - Details */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            overflowY: 'auto',
            paddingRight: '20px'
          }}>
            {/* Property Type & Size */}
                  <div>
              <h3 style={{
                fontFamily: "'Sailors', serif",
                fontSize: '18px',
                color: '#0f084b',
                marginBottom: '16px'
              }}>
                Property Details
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                {(displayProperty.property_type || typeDisplay) && (
                  <div style={{
                    padding: '12px 16px',
                    background: '#f8f8f8',
                    borderRadius: '12px'
                  }}>
                    <div style={{
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      Property Type
                  </div>
                    <div style={{
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: '15px',
                      color: '#0f084b',
                      fontWeight: 600
                    }}>
                      {typeDisplay}
                  </div>
                </div>
                )}
                {(displayProperty.square_footage || sqft) && (
                  <div style={{
                    padding: '12px 16px',
                    background: '#f8f8f8',
                    borderRadius: '12px'
                  }}>
                    <div style={{
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      Square Footage
                    </div>
                    <div style={{
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: '15px',
                      color: '#0f084b',
                      fontWeight: 600
                    }}>
                      {displayProperty.square_footage || sqft} sq ft
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Nearest Airport */}
            {(() => {
              const airportCode = displayProperty.nearest_airport || nearestAirport || (properties && properties.length > 0 ? properties[0]?.nearest_airport : null);
              const airportDistance = displayProperty.driving_distance_miles != null ? displayProperty.driving_distance_miles : 
                                     (airportMiles != null && airportMiles !== '' ? airportMiles : 
                                     (properties && properties.length > 0 ? properties[0]?.driving_distance_miles : null));
              const airportTime = displayProperty.driving_time_minutes != null ? displayProperty.driving_time_minutes : 
                                 (properties && properties.length > 0 ? properties[0]?.driving_time_minutes : null);
              
              return airportCode && airportCode.trim() !== '' ? (
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{
                    fontFamily: "'Sailors', serif",
                    fontSize: '18px',
                    color: '#0f084b',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    Getting There
                  </h3>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px 20px',
                    background: '#f8f8f8',
                    borderRadius: '12px'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#0f084b">
                      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                    </svg>
                    <div>
                      <div style={{
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                        fontSize: '15px',
                        color: '#0f084b',
                        fontWeight: 600,
                        marginBottom: '2px'
                      }}>
                        {airportCode}
                      </div>
                      {(airportDistance != null && airportDistance !== '') || airportTime != null ? (
                        <div style={{
                          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                          fontSize: '13px',
                          color: '#666'
                        }}>
                          {airportDistance != null && airportDistance !== '' ? `${airportDistance} miles` : ''}
                          {airportDistance != null && airportDistance !== '' && airportTime != null ? ' • ' : ''}
                          {airportTime != null ? `${airportTime} min drive` : ''}
                          {airportDistance == null && airportTime == null ? 'away' : ''}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Amenities */}
            {amenitiesFormatted.length > 0 && (
                <div>
                <h3 style={{
                  fontFamily: "'Sailors', serif",
                  fontSize: '18px',
                  color: '#0f084b',
                  marginBottom: '12px'
                }}>
                  Amenities
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {amenitiesFormatted.map((amenity, i) => (
                    <span key={i} style={{
                      padding: '8px 16px',
                      background: '#0f084b',
                      color: '#ffffff',
                      borderRadius: '20px',
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: '14px',
                      fontWeight: 500
                    }}>
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* About Section - Property Description for hosts, Bio for creators */}
            {(profileType === 'creator' ? creator?.bio : displayProperty.description) && (
                <div>
                <h3 style={{
                  fontFamily: "'Sailors', serif",
                  fontSize: '18px',
                  color: '#0f084b',
                  marginBottom: '12px',
                  textAlign: 'center'
                }}>
                  {profileType === 'creator' ? 'About' : 'About This Property'}
                </h3>
                <p style={{
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '15px',
                  color: '#333',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {profileType === 'creator' ? creator?.bio : displayProperty.description}
                </p>
                </div>
            )}

            {/* Media Kit - Only for creators */}
            {profileType === 'creator' && (mediaKitUrl || creator?.media_kit_url) && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '24px',
                marginBottom: '8px'
              }}>
                <a
                  href={mediaKitUrl || creator?.media_kit_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    background: '#0f084b',
                    color: '#ffffff',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: '15px',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" x2="8" y1="13" y2="13"/>
                    <line x1="16" x2="8" y1="17" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                  Download Media Kit
                </a>
              </div>
            )}

            {/* Creator Reviews Section */}
            {profileType === 'creator' && creatorReviews && creatorReviews.length > 0 && (
              <div id="reviews">
                <h3 style={{
                  fontFamily: "'Sailors', serif",
                  fontSize: '18px',
                  color: '#0f084b',
                  marginBottom: '16px'
                }}>
                  Host Reviews {creator?.total_reviews ? `(${creator.total_reviews})` : ''}
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '20px',
                  maxWidth: '100%',
                  margin: '0 auto',
                  padding: '0 20px'
                }}>
                  {creatorReviews.map((review, index) => (
                    <ReviewCard
                      key={`creator-mobile-${index}`}
                      review={review}
                      reviewerName={review.host_name || 'Host'}
                      reviewerAvatar={review.host_avatar_url}
                      collaborationMonth={review.collaboration_month}
                      propertyName={review.property_name}
                      rating={review.rating}
                      reviewTitle={review.review_title}
                      reviewText={review.review_text}
                      response={review.creator_response}
                      responseLabel="Response from Creator"
                      index={index}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Host Reviews Section */}
            {profileType === 'host' && hostReviews && hostReviews.length > 0 && (
              <div id="reviews">
                <h3 style={{
                  fontFamily: "'Sailors', serif",
                  fontSize: '18px',
                  color: '#0f084b',
                  marginBottom: '16px'
                }}>
                  Creator Reviews {host?.total_reviews ? `(${host.total_reviews})` : ''}
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '20px',
                  maxWidth: '100%',
                  margin: '0 auto',
                  padding: '0 20px'
                }}>
                  {hostReviews.map((review, index) => (
                    <ReviewCard
                      key={`host-mobile-${index}`}
                      review={review}
                      reviewerName={review.creator_name || 'Creator'}
                      reviewerAvatar={review.creator_avatar_url}
                      collaborationMonth={review.collaboration_month}
                      propertyName={review.property_name}
                      rating={review.rating}
                      reviewTitle={review.review_title}
                      reviewText={review.review_text}
                      response={review.host_response}
                      responseLabel="Response from Host"
                      index={index}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Collab Opportunity */}
            {(host.description || collabText) && (
                <div>
                <h3 style={{
                  fontFamily: "'Sailors', serif",
                  fontSize: '18px',
                  color: '#0f084b',
                  marginBottom: '12px'
                }}>
                  Collaboration Opportunity
                </h3>
                <div style={{
                  padding: '20px',
                  background: '#f8f8f8',
                  borderRadius: '12px',
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '15px',
                  color: '#333',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {host.description || collabText}
                </div>
              </div>
            )}

            {/* Past Collaborations */}
            {(() => {
              // Parse host partners from PostgreSQL array format if needed
              let hostPartnersParsed = host.partners || [];
              if (typeof hostPartnersParsed === 'string') {
                if (hostPartnersParsed.startsWith('{') && hostPartnersParsed.endsWith('}')) {
                  // PostgreSQL array format: {item1,item2,item3}
                  hostPartnersParsed = hostPartnersParsed
                    .slice(1, -1) // Remove { and }
                    .split(',')
                    .map(p => p.trim().replace(/^"|"$/g, '')) // Remove quotes if present
                    .filter(p => p && p.length > 0); // Filter out empty strings
                } else {
                  try {
                    // Try JSON parse as fallback
                    hostPartnersParsed = JSON.parse(hostPartnersParsed);
                  } catch (e) {
                    hostPartnersParsed = [];
                  }
                }
              }
              // Ensure we have an array
              const displayHostPartners = Array.isArray(hostPartnersParsed) && hostPartnersParsed.length > 0 
                ? hostPartnersParsed 
                : (Array.isArray(hostPartners) && hostPartners.length > 0 ? hostPartners : []);
              
              if (displayHostPartners.length === 0) return null;
              
              return (
                <div style={{ 
                  marginTop: '24px',
                  width: '100%',
                  maxWidth: '600px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  textAlign: 'center'
                }}>
                  <h3 style={{
                    fontFamily: "'Sailors', serif",
                    fontSize: '18px',
                    color: '#0f084b',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>
                    Also Worked With
                  </h3>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '10px',
                    justifyContent: 'center'
                  }}>
                    {displayHostPartners.map((partner, i) => (
                    <span 
                      key={i} 
                      style={{
                        padding: '10px 18px',
                        background: '#ffffff',
                        borderRadius: '24px',
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#0f084b',
                        border: '1.5px solid #e8e8e8',
                        display: 'inline-block',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#dc1d08';
                        e.currentTarget.style.background = '#fff5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e8e8e8';
                        e.currentTarget.style.background = '#ffffff';
                      }}
                    >
                      {partner}
                    </span>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Contact/Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: 'auto',
              paddingTop: '20px'
            }}>
              {isSelf && (
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    padding: '14px 28px',
                    background: '#0f084b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  Edit Profile
                </button>
              )}
              <button
                onClick={() => window.location.href = '/messages'}
                style={{
                  padding: '14px 28px',
                  background: '#dc1d08',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Message Host
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (profileType === 'host' && host && editing) {
      const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
      const imageSrc = (propertyImageUrl || host.banner_url || '')
        ? ((isLocal && (propertyImageUrl || host.banner_url).startsWith('/')) ? `http://localhost:4000${propertyImageUrl || host.banner_url}` : (propertyImageUrl || host.banner_url))
        : '';

      // Editing: mirror HostOnboarding layout
      const header = (
        <>
          <h3 style={{
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
            fontSize: '20px',
            fontWeight: 800,
            color: '#0f084b',
            textAlign: 'center',
            margin: '0 0 -24px'
          }}>Tell us about your property</h3>
          <div style={{
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
            fontSize: 14,
            color: '#0f084b',
            textAlign: 'center',
            opacity: .9
          }}>This helps us match you with the right creators and campaigns.</div>
        </>
      );

      return (
        <>
            {/* Left - Property Image */}
            <div style={{
              position: 'absolute', left: 0, right: '50%', top: '120px', bottom: '80px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
              zIndex: 3, padding: '0 24px'
          }}>
            <label htmlFor="propimg" style={{
              width: 340, maxHeight: 500, minWidth: 340, border: '2px dashed rgba(0,0,0,.3)', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f084b', cursor: 'pointer'
            }}>
              {imageSrc ? (
                <img alt="property" src={imageSrc} style={{ maxWidth: '100%', maxHeight: '500px', display: 'block', borderRadius: 8 }} />
              ) : (
                <div style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 16 }}>Upload Property Image</div>
              )}
            </label>
            <input id="propimg" type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }} onChange={handlePropertyImgChange} />
            <div style={{ ...sectionTitleStyle, marginTop: 12, marginBottom: -4 }}>Collab Opportunity</div>
            <textarea
              style={{ ...inputStyle, border: '2px dashed rgba(0,0,0,.3)', borderRadius: 8, padding: '12px 14px', minHeight: 90, width: 340, minWidth: 340, resize: 'vertical' }}
              value={collabText}
              onChange={(e) => setCollabText(e.target.value)}
              placeholder="Describe the collaboration you're seeking"
            />
            <label style={labelStyle}>What kind of collaboration are you offering?</label>
            
            {/* Simple link for managing properties */}
            {isSelf && properties.length > 0 && (
              <div style={{ marginTop: 30, textAlign: 'center' }}>
                <a 
                  onClick={() => {
                    // Save current changes first
                    const form = document.querySelector('form');
                    if (form) {
                      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                      form.dispatchEvent(submitEvent);
                    }
                    setTimeout(() => {
                      navigate(`/host/onboarding?email=${encodeURIComponent(effectiveEmail)}&add=new`);
                    }, 500);
                  }}
                  style={{
                    color: '#0f084b',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: 14,
                    display: 'inline-block'
                  }}
                >
                  + Add another property
                </a>
              </div>
            )}
          </div>

          {/* Right - Form */}
          <div style={{
            position: 'absolute', left: '50%', right: 0, top: '200px', bottom: '80px', display: 'flex',
            flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', zIndex: 3, padding: '0 64px'
          }}>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: '100%' }}>
              {header}

              <div>
                <input style={inputStyle} value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="" />
                <label style={labelStyle}>Property Name</label>
              </div>
              <div>
                <input 
                  style={inputStyle} 
                  value={hostHandle} 
                  onChange={(e) => setHostHandle(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))} 
                  placeholder="yourpropertyhandle" 
                />
                <label style={labelStyle}>Handle (username)</label>
              </div>
              <div>
                <LocationAutocomplete
                  type="country"
                  value={hostCountry}
                  onChange={handleHostCountryChange}
                  placeholder="Start typing country..."
                  label="Country"
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <LocationAutocomplete
                    type="state"
                    value={hostState}
                    onChange={handleHostStateChange}
                    placeholder={hostCountry === "United States" ? "Start typing state..." : "State (optional)"}
                    label="State"
                    disabled={hostCountry && hostCountry !== "United States"}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <LocationAutocomplete
                    type="city"
                    value={hostCity}
                    onChange={setHostCity}
                    stateValue={hostState}
                    placeholder={hostState ? "Start typing city..." : hostCountry === "United States" ? "Select state first" : "Start typing city..."}
                    label="City"
                    disabled={hostCountry === "United States" && !hostState}
                  />
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6 }}>
                  {PROPERTY_TYPES.map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setType(t)}
                      aria-pressed={type === t}
                      style={{
                        borderRadius: 18,
                        padding: '6px 12px',
                        border: '1px solid rgba(0,0,0,.2)',
                        cursor: 'pointer',
                        background: type === t ? '#0f084b' : 'transparent',
                        color: type === t ? '#fff' : '#0f084b',
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                        fontSize: 12,
                      }}>
                      {t.replace(/_/g,' ')}
                    </button>
                  ))}
                </div>
                <label style={labelStyle}>Property Type</label>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <input style={inputStyle} type="text" inputMode="numeric" pattern="[0-9]*" value={bedrooms} onChange={(e) => setBedrooms(e.target.value.replace(/[^0-9]/g, ''))} placeholder="" />
                  <label style={labelStyle}># Bedrooms</label>
                </div>
                <div style={{ flex: 1 }}>
                  <input style={inputStyle} type="number" inputMode="decimal" step="0.5" value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} placeholder="" />
                  <label style={labelStyle}># Bathrooms</label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <input style={inputStyle} type="text" inputMode="numeric" pattern="[0-9]*" value={sqft} onChange={(e) => setSqft(e.target.value.replace(/[^0-9]/g, ''))} placeholder="" />
                  <label style={labelStyle}>Square Footage</label>
                </div>
                <div style={{ flex: 1 }}>
                  <input style={inputStyle} value={nearestAirport} onChange={(e) => setNearestAirport(e.target.value)} placeholder="" />
                  <label style={labelStyle}>Nearest Airport</label>
                </div>
              </div>
              <div>
                <input style={inputStyle} type="text" inputMode="numeric" pattern="[0-9]*" value={airportMiles} onChange={(e) => setAirportMiles(e.target.value.replace(/[^0-9]/g, ''))} placeholder="" />
                <label style={labelStyle}>Driving distance (miles) from nearest airport</label>
              </div>
              <div>
                <input style={inputStyle} value={linktree} onChange={(e) => setLinktree(e.target.value)} placeholder="" />
                <label style={labelStyle}>Website</label>
              </div>

              {/* Listings */}
              {hostCategory !== 'tourism_board' && (
              <div>
                <input style={inputStyle} value={directUrl} onChange={(e) => setDirectUrl(e.target.value)} placeholder="" />
                <label style={labelStyle}>Direct Booking Link</label>
              </div>
              )}
              {hostCategory !== 'tourism_board' && (
              <div>
                <input style={inputStyle} value={airbnbUrl} onChange={(e) => setAirbnbUrl(e.target.value)} placeholder="" />
                <label style={labelStyle}>Airbnb Link</label>
              </div>
              )}
              {hostCategory !== 'tourism_board' && (
              <div>
                <input style={inputStyle} value={vrboUrl} onChange={(e) => setVrboUrl(e.target.value)} placeholder="" />
                <label style={labelStyle}>Vrbo Link</label>
              </div>
              )}
              {hostCategory !== 'tourism_board' && (
              <div>
                <input style={inputStyle} value={otherListingUrl} onChange={(e) => setOtherListingUrl(e.target.value)} placeholder="" />
                <label style={labelStyle}>Other Listing Link</label>
              </div>
              )}

              {/* Socials (URLs only) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <input style={inputStyle} value={igUrl} onChange={(e) => setIgUrl(e.target.value)} />
                  <label style={labelStyle}>Instagram URL</label>
                </div>
                <div>
                  <input style={inputStyle} value={ttUrl} onChange={(e) => setTtUrl(e.target.value)} />
                  <label style={labelStyle}>TikTok URL</label>
                </div>
                <div>
                  <input style={inputStyle} value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} />
                  <label style={labelStyle}>YouTube URL</label>
                </div>
                {hostCategory !== 'tourism_board' && hostCategory !== 'boutique_hotel' && (
                <div>
                  <input style={inputStyle} value={otherUrl} onChange={(e) => setOtherUrl(e.target.value)} />
                  <label style={labelStyle}>LTK URL</label>
                </div>
                )}
              </div>

              {/* Amenities */}
              <div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6 }}>
                  {AMENITIES.map((a) => (
                    <button type="button" key={a} onClick={() => toggleAmenity(a)}
                      style={{
                        borderRadius: 18, padding: '6px 12px', border: '1px solid rgba(0,0,0,.2)', cursor: 'pointer',
                        background: amenities.includes(a) ? '#0f084b' : 'transparent', color: amenities.includes(a) ? '#fff' : '#0f084b',
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12,
                      }}>{AMENITY_LABELS[a] || a}</button>
                  ))}
                </div>
                <label style={labelStyle}>Property Amenities</label>
              </div>

              {/* Partners */}
              <div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6, marginBottom: 8 }}>
                  {hostPartners.map((p, i) => (
                    <Chip key={`${p}-${i}`} text={p} onRemove={() => setHostPartners((arr) => arr.filter((_, idx) => idx !== i))} />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inputStyle, flex: 1 }} value={hostPartnerInput} onChange={(e) => setHostPartnerInput(e.target.value)} placeholder="Add partner" />
                  <button type="button" onClick={addHostPartnerChip} style={{
                    background: '#0f084b', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 14
                  }}>Add</button>
                </div>
                <label style={labelStyle}>Collaboration Partners (up to 3)</label>
              </div>

              <button type="submit" style={{
                background: '#dc1d08', color: '#fff', border: 'none', borderRadius: 8,
                padding: '14px 32px', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 18, fontWeight: 800, letterSpacing: '0.08em',
                cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textTransform: 'uppercase'
              }}>Save Property</button>
            </form>
          </div>
        </>
      );
    }

    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileType, creator, host, hostCategory, property, properties, showcase, propertyShowcase, editing, isMobile, creatorAvatarUrl, propertyImageUrl, brandName, ownerName, hostCity, hostState, directUrl, airbnbUrl, vrboUrl, otherListingUrl, linktree, igUrl, ttUrl, ytUrl, otherUrl, creatorCity, creatorState, collabText, selectedCats, creatorPartners, prefs, hostPartners, amenities, type, bedrooms, bathrooms, sqft, nearestAirport, airportMiles, reviews, creatorReviews, hostReviews]);

  // Calculate title BEFORE any returns (React Hooks must come before conditional returns)
  const title = useMemo(() => {
    if (profileType === 'host') return 'Host Profile';
    if (profileType === 'creator') return 'Creator Profile';
    return 'Profile';
  }, [profileType]);

  // Property management section removed - this belongs in the host dashboard only
  const propertyManagementSection = null;
  
  // Old property management code - kept for reference but not rendered
  const _unusedPropertyManagement = false ? (
    <div style={{
      marginTop: 40,
      padding: '32px 48px',
      background: 'rgba(248, 248, 248, 0.9)',
      borderRadius: 12,
      minHeight: 200
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24
      }}>
        <h3 style={{
          fontFamily: "'Sailors', serif",
          fontSize: 24,
          color: '#0f084b',
          margin: 0
        }}>Your Properties</h3>
        
        <button
          onClick={() => navigate(`/host/onboarding?email=${encodeURIComponent(effectiveEmail)}&add=new`)}
          style={{
            background: '#dc1d08',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            cursor: 'pointer',
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
            fontSize: 14,
            fontWeight: 600
          }}
        >
          + Add Property
        </button>
      </div>
      
      {properties.length > 0 ? (
        <div className="profile-properties-grid">
          <h3>Other Properties</h3>
          <div className="properties-grid">
          {properties.map(prop => (
            <PropertyCard 
              key={prop.id}
              property={prop}
              onEdit={() => {
                // For now, navigate to host onboarding to edit
                navigate(`/host/onboarding?email=${encodeURIComponent(effectiveEmail)}&edit=${prop.id}`);
              }}
              onDelete={() => handleDeleteProperty(prop.id)}
            />
          ))}
          </div>
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#666',
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
        }}>
          <p style={{ fontSize: 16, marginBottom: 20 }}>
            You haven't added any properties yet. Add your first property to start receiving collaboration opportunities!
          </p>
          <button
            onClick={() => navigate(`/host/onboarding?email=${encodeURIComponent(effectiveEmail)}&add=new`)}
            style={{
              background: '#dc1d08',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              cursor: 'pointer',
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
              fontSize: 16,
              fontWeight: 600
            }}
          >
            Add Your First Property
          </button>
        </div>
      )}
    </div>
  ) : null;

  // Only show property management section in dashboard view (not on public profiles)
  const below = (
    <>
      {belowContent}
      {/* Property management only shows in dashboard context, not public profiles */}
      {propertyManagementSection}
    </>
  );
  const isHostEdit = editing && profileType === 'host';
  const isCreatorEdit = editing && profileType === 'creator';
  // Use same layout sizing for creators and hosts so spacing aligns
  if (authorized !== true) {
    return null;
  }
  return (
    <>
      <InfoPage 
        title={title} 
        divider={false}
        below={below} 
        disableStamps 
        tallOverride={false} 
        customMinHeight={undefined}
      />
      {/* Creator Edit Modal */}
      {creatorEditOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000 }}>
          <div style={{ width:520, maxWidth:'92%', background:'#fff', borderRadius:12, boxShadow:'0 20px 60px rgba(0,0,0,.25)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #eee', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontFamily:"'Sailors', serif", fontSize:20, color:'#0f084b' }}>Edit Creator Profile</div>
              <button onClick={()=>setCreatorEditOpen(false)} style={{ background:'none', border:'none', fontSize:24, cursor:'pointer', color:'#666' }}>×</button>
            </div>
            <div style={{ padding:20, display:'grid', rowGap:14 }}>
              <label style={{ fontFamily:"'Neue Einstellung', 'Poppins', sans-serif", color:'#0f084b', fontSize:13 }}>First name</label>
              <input value={creatorEdit.firstName} onChange={(e)=>setCreatorEdit({...creatorEdit, firstName:e.target.value})} style={{ padding:10, border:'1px solid #e6e8ec', borderRadius:8 }} />
              <label style={{ fontFamily:"'Neue Einstellung', 'Poppins', sans-serif", color:'#0f084b', fontSize:13 }}>Last name</label>
              <input value={creatorEdit.lastName} onChange={(e)=>setCreatorEdit({...creatorEdit, lastName:e.target.value})} style={{ padding:10, border:'1px solid #e6e8ec', borderRadius:8 }} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontFamily:"'Neue Einstellung', 'Poppins', sans-serif", color:'#0f084b', fontSize:13 }}>City</label>
                  <input value={creatorEdit.locationCity} onChange={(e)=>setCreatorEdit({...creatorEdit, locationCity:e.target.value})} style={{ width:'100%', padding:10, border:'1px solid #e6e8ec', borderRadius:8 }} />
                </div>
                <div>
                  <label style={{ fontFamily:"'Neue Einstellung', 'Poppins', sans-serif", color:'#0f084b', fontSize:13 }}>State</label>
                  <input value={creatorEdit.locationState} onChange={(e)=>setCreatorEdit({...creatorEdit, locationState:e.target.value})} style={{ width:'100%', padding:10, border:'1px solid #e6e8ec', borderRadius:8 }} />
                </div>
              </div>
              <label style={{ fontFamily:"'Neue Einstellung', 'Poppins', sans-serif", color:'#0f084b', fontSize:13 }}>Profile image URL</label>
              <input value={creatorEdit.avatarUrl} onChange={(e)=>setCreatorEdit({...creatorEdit, avatarUrl:e.target.value})} style={{ padding:10, border:'1px solid #e6e8ec', borderRadius:8 }} placeholder="https://..." />
              <label style={{ fontFamily:"'Neue Einstellung', 'Poppins', sans-serif", color:'#0f084b', fontSize:13 }}>About</label>
              <textarea value={creatorEdit.bio} onChange={(e)=>setCreatorEdit({...creatorEdit, bio:e.target.value})} style={{ padding:10, border:'1px solid #e6e8ec', borderRadius:8, minHeight:100, resize:'vertical' }} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontFamily:"'Neue Einstellung', 'Poppins', sans-serif", color:'#0f084b', fontSize:13 }}>Age Range</label>
                  <select value={creatorEdit.ageRange} onChange={(e)=>setCreatorEdit({...creatorEdit, ageRange:e.target.value})} style={{ width:'100%', padding:10, border:'1px solid #e6e8ec', borderRadius:8, backgroundColor:'white', cursor:'pointer' }}>
                    <option value="">Select age range</option>
                    <option value="18-25">18-25</option>
                    <option value="26-36">26-36</option>
                    <option value="37-50">37-50</option>
                    <option value="51-65">51-65</option>
                    <option value="66+">66 and above</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontFamily:"'Neue Einstellung', 'Poppins', sans-serif", color:'#0f084b', fontSize:13 }}>Gender</label>
                  <select value={creatorEdit.gender} onChange={(e)=>setCreatorEdit({...creatorEdit, gender:e.target.value})} style={{ width:'100%', padding:10, border:'1px solid #e6e8ec', borderRadius:8, backgroundColor:'white', cursor:'pointer' }}>
                    <option value="">Select gender</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
            <div style={{ padding:16, borderTop:'1px solid #eee', display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setCreatorEditOpen(false)} style={{ padding:'10px 16px', border:'1px solid #e6e8ec', background:'#fff', borderRadius:8, cursor:'pointer' }}>Cancel</button>
              <button onClick={async ()=>{
                try {
                  const resp = await fetch(`${apiBase}/api/creator/profile`, { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({
                    email: profileEmail || effectiveEmail || localEmail,
                    firstName: creatorEdit.firstName,
                    lastName: creatorEdit.lastName,
                    locationCity: creatorEdit.locationCity,
                    locationState: creatorEdit.locationState,
                    avatarUrl: creatorEdit.avatarUrl,
                    bio: creatorEdit.bio,
                    ageRange: creatorEdit.ageRange,
                    gender: creatorEdit.gender
                  })});
                  if (resp.ok) {
                    if (creator) {
                      creator.first_name = creatorEdit.firstName;
                      creator.last_name = creatorEdit.lastName;
                      creator.location_city = creatorEdit.locationCity;
                      creator.location_state = creatorEdit.locationState;
                      creator.avatar_url = creatorEdit.avatarUrl;
                      creator.bio = creatorEdit.bio;
                      creator.age_range = creatorEdit.ageRange;
                      creator.gender = creatorEdit.gender;
                    }
                    setCreatorAvatarUrl(creatorEdit.avatarUrl || creatorAvatarUrl);
                    setCreatorCity(creatorEdit.locationCity);
                    setCreatorState(creatorEdit.locationState);
                    setCreatorEditOpen(false);
                  }
                } catch (_) {}
              }} style={{ padding:'10px 16px', border:'none', background:'#0f084b', color:'#fff', borderRadius:8, cursor:'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Profile;